import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, formatMoney } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, CheckCircle2, Circle, Loader2, X, Wallet } from "lucide-react";
import PlanItemPaymentDialog from "@/components/PlanItemPaymentDialog";

const PLAN_STATUSES = [
  { key: "planeado",   label: "Planeado",   tone: "bg-muted text-muted-foreground", icon: Circle },
  { key: "en_proceso", label: "En proceso", tone: "bg-accent/20 text-accent",       icon: Loader2 },
  { key: "realizado",  label: "Realizado",  tone: "bg-primary/15 text-primary",     icon: CheckCircle2 },
  { key: "cancelado",  label: "Cancelado",  tone: "bg-destructive/15 text-destructive", icon: X },
];

export default function TreatmentPlan({ patientId, treatments, onChange }) {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({ name: "", treatment_id: "", tooth: "", price: "", notes: "" });
  const [payItem, setPayItem] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/patients/${patientId}/plan`);
    setItems(data);
  };
  useEffect(() => { load(); }, [patientId]); // eslint-disable-line

  const addItem = async (e) => {
    e?.preventDefault();
    if (!draft.name.trim()) { toast.error("Ingresa una descripción"); return; }
    try {
      await api.post(`/patients/${patientId}/plan`, {
        name: draft.name.trim(),
        treatment_id: draft.treatment_id || null,
        tooth: draft.tooth || "",
        price: Number(draft.price) || 0,
        notes: draft.notes || "",
        status: "planeado",
      });
      setDraft({ name: "", treatment_id: "", tooth: "", price: "", notes: "" });
      load(); onChange && onChange();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const onTreatmentPick = (tid) => {
    const t = treatments.find((x) => x.id === tid);
    setDraft((d) => ({
      ...d,
      treatment_id: tid,
      name: t ? t.name : d.name,
      price: t ? t.price : d.price,
    }));
  };

  const setStatus = async (id, status) => {
    const it = items.find((x) => x.id === id);
    if (status === "realizado" && it && it.status !== "realizado" && !it.cash_txn_id) {
      setPayItem(it);
      return;
    }
    try {
      await api.patch(`/plan-items/${id}/status`, null, { params: { status } });
      load(); onChange && onChange();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo actualizar"); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este item del plan?")) return;
    try { await api.delete(`/plan-items/${id}`); load(); onChange && onChange(); }
    catch { toast.error("No se pudo eliminar"); }
  };

  const totalPlaneado = items.filter((i) => i.status === "planeado" || i.status === "en_proceso").reduce((s, i) => s + (i.price || 0), 0);
  const totalRealizado = items.filter((i) => i.status === "realizado").reduce((s, i) => s + (i.price || 0), 0);

  return (
    <div className="space-y-4" data-testid="treatment-plan">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="card-flat p-4">
          <div className="label-eyebrow">Pendiente</div>
          <div className="font-mono font-bold text-2xl mt-1 text-accent">{formatMoney(totalPlaneado)}</div>
        </div>
        <div className="card-flat p-4">
          <div className="label-eyebrow">Realizado</div>
          <div className="font-mono font-bold text-2xl mt-1 text-primary">{formatMoney(totalRealizado)}</div>
        </div>
        <div className="card-flat p-4">
          <div className="label-eyebrow">Total plan</div>
          <div className="font-mono font-bold text-2xl mt-1">{formatMoney(totalPlaneado + totalRealizado)}</div>
        </div>
      </div>

      <form onSubmit={addItem} className="card-flat p-4 grid grid-cols-2 sm:grid-cols-12 gap-2" data-testid="plan-add-form">
        <select className="input-flat sm:col-span-3" value={draft.treatment_id} onChange={(e) => onTreatmentPick(e.target.value)} data-testid="plan-treatment">
          <option value="">— Tratamiento —</option>
          {treatments.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <input className="input-flat sm:col-span-3" placeholder="Descripción" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="plan-name" />
        <input className="input-flat font-mono sm:col-span-1" placeholder="Diente" value={draft.tooth} onChange={(e) => setDraft({ ...draft, tooth: e.target.value })} data-testid="plan-tooth" />
        <input type="number" step="1000" className="input-flat font-mono sm:col-span-2" placeholder="Precio" value={draft.price} onChange={(e) => setDraft({ ...draft, price: e.target.value })} data-testid="plan-price" />
        <input className="input-flat sm:col-span-2" placeholder="Notas" value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
        <button type="submit" className="btn-primary justify-center sm:col-span-1" data-testid="plan-add-btn">
          <Plus className="w-4 h-4" />
        </button>
      </form>

      <div className="card-flat overflow-hidden">
        <div className="hidden md:grid grid-cols-12 px-4 py-3 border-b border-border bg-muted/40">
          <div className="col-span-4 label-eyebrow">Procedimiento</div>
          <div className="col-span-1 label-eyebrow">Diente</div>
          <div className="col-span-2 label-eyebrow">Estado</div>
          <div className="col-span-3 label-eyebrow">Notas</div>
          <div className="col-span-1 label-eyebrow text-right">Precio</div>
          <div className="col-span-1"></div>
        </div>
        {items.length === 0 && (
          <div className="px-4 py-12 text-center text-sm text-muted-foreground">No hay procedimientos planeados.</div>
        )}
        {items.map((it) => (
          <div key={it.id} className="grid grid-cols-2 md:grid-cols-12 gap-y-2 px-4 py-3 border-b border-border last:border-b-0 items-center hover:bg-muted/40" data-testid={`plan-row-${it.id}`}>
            <div className="col-span-2 md:col-span-4 text-sm font-semibold truncate">
              {it.name}
              <span className="md:hidden font-mono text-xs text-muted-foreground font-normal ml-2">{it.tooth ? `Diente ${it.tooth}` : ""}</span>
            </div>
            <div className="hidden md:block col-span-1 font-mono text-xs text-muted-foreground">{it.tooth || "—"}</div>
            <div className="md:col-span-2">
              <select
                value={it.status}
                onChange={(e) => setStatus(it.id, e.target.value)}
                className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-sm bg-card border border-border focus:ring-2 focus:ring-primary outline-none"
                data-testid={`plan-status-${it.id}`}
              >
                {PLAN_STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            <div className="hidden md:block col-span-3 text-xs text-muted-foreground truncate">{it.notes || "—"}</div>
            <div className="md:col-span-1 flex md:block items-center justify-end gap-2 text-right font-mono text-sm">
              <div>
                {formatMoney(it.price)}
                {it.cash_txn_id && (
                  <div className="text-[9px] uppercase tracking-widest text-primary font-bold flex items-center justify-end gap-1" data-testid={`plan-paid-${it.id}`}>
                    <Wallet className="w-3 h-3" /> {it.payment_method}
                  </div>
                )}
              </div>
              <button data-testid={`plan-delete-${it.id}`} onClick={() => remove(it.id)} className="md:hidden p-2 -mr-2 text-muted-foreground hover:text-destructive" aria-label="Eliminar">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <div className="hidden md:flex col-span-1 justify-end">
              <button data-testid={`plan-delete-${it.id}`} onClick={() => remove(it.id)} className="p-2 text-muted-foreground hover:text-destructive" aria-label="Eliminar">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
      <PlanItemPaymentDialog
        open={!!payItem}
        onOpenChange={(o) => { if (!o) setPayItem(null); }}
        item={payItem}
        onDone={() => { load(); onChange && onChange(); }}
      />
    </div>
  );
}
